// AdminLayout.tsx — Sidebar nav + header + main content shell.
//
// Three-zone layout: sticky topbar → sidebar (tab strip on mobile,
// vertical on ≥1100px) → main content area.
//
// Header:
//   - DominusMark (mark-only, size 32) per Brand Bible Ch 6
//   - "DOMINUS" in Cinzel Decorative (separate styled text, not lockup)
//   - "Operator Console" section tag in Cinzel
//   - Status dot + "All systems nominal" + principal + sign out
//
// Renders all 9 sections in a single scroll document. Sidebar items
// are anchor links; scroll position handled by NavSidebar.

import React, { useCallback, useState } from 'react';
import DominusMark from './components/brand/DominusMark';
import NavSidebar, { type PageId } from './components/NavSidebar';
import MockDataBanner from './components/MockDataBanner';
import OverviewPage from './pages/OverviewPage';
import NewsPage from './pages/NewsPage';
import OperationsPage from './pages/OperationsPage';
import BusinessPage from './pages/BusinessPage';
import GameplayPage from './pages/GameplayPage';
import UsersPage from './pages/UsersPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ControlsPage from './pages/ControlsPage';
import AuditLogPage from './pages/AuditLogPage';
import type { UseAdminAuthReturn } from './hooks/useAdminAuth';
import { AdminSessionProvider, useAdminSessionContext } from './hooks/useAdminSession';

interface AdminLayoutProps {
  auth: UseAdminAuthReturn;
}

// Truncate principal to first 8 + last 4 chars for topbar display.
function shortPrincipal(p: string): string {
  if (p.length <= 14) return p;
  return `${p.slice(0, 8)}…${p.slice(-4)}`;
}

// ---------------------------------------------------------------------------
// Inner layout — rendered inside AdminSessionProvider so it can consume the
// session context for the revoke-before-logout flow.
// ---------------------------------------------------------------------------

function AdminLayoutInner({ auth }: AdminLayoutProps): React.ReactElement {
  const [activePage, setActivePage] = useState<PageId>('overview');
  const session = useAdminSessionContext();

  // DEV BYPASS banner gate: env var AND non-production network.
  // Mirrors the double-guard in useAdminAuth.ts — both must be true.
  const devBypassActive =
    import.meta.env.VITE_ADMIN_DEV_BYPASS === 'true' &&
    import.meta.env.VITE_DFX_NETWORK !== 'ic';

  // Admin canister not-yet-deployed banner.
  // Shown in production when VITE_ADMIN_CANISTER_ID is blank or the
  // sentinel placeholder written into .env.production. Prevents the
  // operator from seeing a blank page or an unhandled actor-parse error.
  // Cleared automatically once the real mainnet ID is set in Cloudflare
  // Pages env vars (docs/launch/admin-deploy-runbook.md §Step 4).
  const adminCanisterId = import.meta.env.VITE_ADMIN_CANISTER_ID ?? '';
  const adminCanisterPending =
    adminCanisterId === '' ||
    adminCanisterId === 'REPLACE_AFTER_MAINNET_DEPLOY';

  // L-33 (2026-05-17): admin API token not-yet-configured banner.
  // Mirrors the adminCanisterPending pattern. Shown when VITE_ADMIN_API_TOKEN
  // is blank or the sentinel placeholder. Without this, the operator sees
  // silent 401s from the Worker on every admin action (Stripe lookup, invite
  // dispatch, daily-metrics test-fire). Cleared automatically once the real
  // token is set in Cloudflare Pages env vars.
  const adminApiToken = import.meta.env.VITE_ADMIN_API_TOKEN ?? '';
  const adminApiTokenPending =
    adminApiToken === '' ||
    adminApiToken === 'REPLACE_IN_CLOUDFLARE_PAGES';

  // Revoke the canister session token before completing the II logout.
  // If revoke fails (canister unreachable), proceed with logout anyway —
  // the token will expire on its own within the canister-set window.
  const [isSigningOut, setIsSigningOut] = useState(false);
  const handleSignOut = useCallback(async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await session.revoke();
    } catch {
      // Non-fatal — token self-expires.
    }
    try {
      await auth.logout();
    } finally {
      // logout() typically triggers a redirect; the finally is defensive.
      setIsSigningOut(false);
    }
  }, [session, auth, isSigningOut]);

  return (
    <>
      <a className="skip-link" href="#main">Skip to content</a>

      {devBypassActive && (
        <div className="dev-bypass-banner" role="status" aria-live="polite">
          DEV BYPASS — admin allowlist disabled. Production enforces dominus_admin gate.
        </div>
      )}

      {adminCanisterPending && (
        <div className="canister-pending-banner" role="status" aria-live="polite">
          Admin canister not yet deployed to mainnet. Metrics, auto-top-up, and controls
          are unavailable until <code>VITE_ADMIN_CANISTER_ID</code> is set in Cloudflare
          Pages env vars. See <em>docs/launch/admin-deploy-runbook.md §Step 4</em>.
        </div>
      )}

      {adminApiTokenPending && (
        <div className="canister-pending-banner" role="status" aria-live="polite">
          Admin API token not yet configured. Worker-side admin actions (Stripe lookup,
          invite dispatch, daily-metrics test-fire) will return 401 until
          <code>VITE_ADMIN_API_TOKEN</code> is set in Cloudflare Pages env vars.
          See <em>docs/launch/admin-deploy-runbook.md §Step 4</em>.
        </div>
      )}

      <div className="console">

        {/* ── TOP BAR ────────────────────────────────────────────────────────── */}
        <header className="topbar" role="banner">
          <div className="topbar-brand">
            {/* DominusMark mark-only, size 32 per Brand Bible Ch 6 */}
            <DominusMark size={32} className="topbar-mark" />
            {/* Wordmark as separate Cinzel Decorative text — not a lockup SVG */}
            <span className="topbar-wordmark">DOMINUS</span>
            <span className="topbar-section-tag">Operator Console</span>
          </div>

          <div className="topbar-meta">
            {/* Status dot + nominal status */}
            <span>
              <span className="topbar-status-dot" aria-hidden="true" />
              All systems nominal
            </span>
            <span className="topbar-divider" aria-hidden="true" />
            {/* Signed-in principal (truncated) */}
            <span className="topbar-operator" title={auth.principal ?? ''}>
              {auth.principal ? shortPrincipal(auth.principal) : '—'}
            </span>
            <span className="topbar-divider" aria-hidden="true" />
            {/* Revoke session token before II logout (Phase 3 — session-token bridge) */}
            <button
              className="topbar-signout"
              onClick={() => { handleSignOut().catch(console.error); }}
              disabled={isSigningOut}
            >
              {isSigningOut ? 'Signing out' : 'Sign out'}
            </button>
          </div>
        </header>

        {/* ── BODY GRID ──────────────────────────────────────────────────────── */}
        <div className="body-grid">

          {/* ── SIDEBAR ──────────────────────────────────────────────────────── */}
          <NavSidebar activePage={activePage} onNavigate={setActivePage} />

          {/* ── MAIN CONTENT ─────────────────────────────────────────────────── */}
          <main id="main">
            <MockDataBanner />

            {/* Section I — Overview (full implementation, Phase A) */}
            <OverviewPage identity={auth.identity} />

            {/* Section II — ICP News (full implementation, Phase A) */}
            <NewsPage />

            {/* Sections III–IX — live data, Phase B */}
            <OperationsPage identity={auth.identity} />
            <BusinessPage identity={auth.identity} />
            <GameplayPage identity={auth.identity} />
            <UsersPage identity={auth.identity} />
            <AnalyticsPage />
            <ControlsPage identity={auth.identity} />
            <AuditLogPage identity={auth.identity} />

            {/* Footer */}
            <footer>
              <p className="footer-copy">
                © 2026 DOMINUS · Operator Console · Restricted access
              </p>
            </footer>
          </main>

        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Exported component — wraps AdminLayoutInner in AdminSessionProvider so the
// session token is issued as soon as II login completes (identity is non-null).
// ---------------------------------------------------------------------------

export default function AdminLayout({ auth }: AdminLayoutProps): React.ReactElement {
  return (
    <AdminSessionProvider identity={auth.identity}>
      <AdminLayoutInner auth={auth} />
    </AdminSessionProvider>
  );
}
